<template>
  <BittsModal
    :visible="true"
    :width="400"
    :title="`Delete ${presetName}?`"
    :icon="['fak', 'delete']"
    icon-color="danger"
    variant="confirm"
    primary-button-text="Delete preset"
    primary-button-type="danger"
    secondary-button-text="Cancel"
    :action-loading="isDeleting"
    :disabled="!newDefaultPresetId"
    class="c-field-preset-delete-default-modal"
    :class="{
      'c-field-preset-delete-default-modal--with-picker': !singleReplacement,
    }"
    @action="emit('confirm', newDefaultPresetId)"
    @closed="emit('closed')"
  >
    <template #description>
      This is your default field preset.
      <template v-if="singleReplacement">
        Deleting it will make
        <strong class="text-neutral-text-strong">
          {{ singleReplacement.name }}
        </strong>
        your new default field preset.
      </template>
      <template v-else>
        <br />
        Choose which preset takes over before deleting.
      </template>
    </template>
    <BittsSelect
      v-if="!singleReplacement"
      v-model="newDefaultPresetId"
      form-label="New default field preset"
      :options="replacementOptions"
      :searchable="false"
      class="c-field-preset-delete-default-modal__select"
    />
  </BittsModal>
</template>

<script setup lang="ts">
import { BittsModal, BittsSelect } from '@crossbeam/bitts';

import { computed, ref } from 'vue';

type ReplacementPresetType = { id: number; name: string | null };

const { presetName, replacementPresets, isDeleting } = defineProps<{
  presetName: string;
  replacementPresets: ReplacementPresetType[];
  isDeleting: boolean;
}>();

const emit = defineEmits<{
  confirm: [newDefaultPresetId: number | null];
  closed: [];
}>();

const singleReplacement = computed(() =>
  replacementPresets.length === 1 ? replacementPresets[0] : null,
);

const replacementOptions = computed(() =>
  replacementPresets.map((preset) => ({
    label: preset.name,
    value: preset.id,
  })),
);

const newDefaultPresetId = ref<number | null>(
  replacementPresets[0]?.id ?? null,
);
</script>

<style lang="pcss" scoped>
@reference "../../css/theme.css";

/* Spacing from the designs, which the confirm variant doesn't match */
.c-field-preset-delete-default-modal:deep()
  dialog
  .o-bitts-modal__header
  .icon-background.danger {
  @apply w-auto! h-auto! p-0! pt-4! mb-20! rounded-none! bg-none!;
}

/* The designs' 48px icon frame holds a 40px glyph */
.c-field-preset-delete-default-modal:deep() .icon-background .icon {
  @apply mt-0! text-[40px]!;
}

.c-field-preset-delete-default-modal:deep() .o-bitts-modal__header {
  @apply pb-40!;
}

.c-field-preset-delete-default-modal:deep() .o-bitts-modal__title {
  @apply mb-4!;
}

.c-field-preset-delete-default-modal:deep() .o-bitts-modal__description {
  @apply m-0! text-base!;
}

.c-field-preset-delete-default-modal:deep() .o-bitts-modal__footer {
  @apply px-16! gap-12!;
}

.c-field-preset-delete-default-modal--with-picker:deep()
  .o-bitts-modal__header {
  @apply pb-24!;
}

/* The confirm variant hides its content area, where the picker lives */
.c-field-preset-delete-default-modal--with-picker:deep()
  .o-bitts-modal__content {
  @apply block! px-24! pb-24!;
}

.c-field-preset-delete-default-modal__select {
  @apply text-left w-full;
}

.c-field-preset-delete-default-modal__select:deep(.ant-select) {
  @apply w-full;
}
</style>
